import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Icon, type IIconProps, type ITooltipProps, Radio, Tooltip, Typography } from 'shared/ui'
import styles from './styles.module.scss'

type IRadioVariant = 'square' | 'rounded' | 'inline' | 'squareInset'

export type IRadioBoxProps<T> = {
  id: string | number
  value: T
  text: string
  tooltip?: {
    TooltipProps?: ITooltipProps
    IconProps?: IIconProps
  }
  variant?: IRadioVariant
  onChange?: (value: T) => void
  active?: boolean
  rightContent?: React.ReactNode
  disabled?: boolean
}

type IRadioListProps<T> = {
  active?: T | null
  variant?: IRadioVariant
  options: IRadioBoxProps<T>[]
  onChange?: (value: T) => void
}

export const RadioOption = observer(<T,>(option: IRadioBoxProps<T>) => {
  const { onChange, active, text, tooltip, rightContent, variant, disabled } = option
  const onClick = () => {
    onChange?.(option.value)
  }

  return (
    <div
      className={classNames(styles.item, {
        [styles.active]: active,
        [styles.inlineVariant]: variant === 'inline',
        [styles.disabled]: disabled,
      })}
      onClick={disabled ? undefined : onClick}
    >
      <div className={styles.item__content}>
        <Radio size={'small'} checked={active} disabled={disabled} />
        <Typography
          className={styles.item__text}
          variant={'body-md-regular'}
          ariaLabel={'Set as Primary'}
        >
          {text}
        </Typography>

        {tooltip && (
          <Tooltip className={styles.item__tooltip} {...tooltip.TooltipProps}>
            <Icon icon={'question'} fontSize={14} {...tooltip.IconProps} />
          </Tooltip>
        )}

        {rightContent && <div className={styles.item__rightContent}>{rightContent}</div>}
      </div>
    </div>
  )
})

export const RadioOptions = observer(
  <T,>({ options, active, variant = 'square', onChange }: IRadioListProps<T>) => {
    return (
      <div
        className={classNames(styles.wrap, {
          [styles.squareVariant]: variant === 'square',
          [styles.roundedVariant]: variant === 'rounded',
          [styles.inlineVariant]: variant === 'inline',
          [styles.squareInsetVariant]: variant === 'squareInset',
        })}
      >
        {options.map((option) => {
          return (
            <RadioOption
              key={option.id}
              active={active === option.value}
              onChange={onChange}
              variant={variant}
              {...option}
            />
          )
        })}
      </div>
    )
  }
)
